import * as React from 'react';
import * as autoBind from 'react-autobind';
import ParentCommentContainer from '../ParentComment/ParentCommentContainer';
import Search from '../Search/Search';
import CommentModel from '../../types/CommentModel';
import LocalStorage from '../../services/LocalStorageWrapper/LocalStorageWrapper';
import {ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import Strings from '../../globals/Strings';
import Constants, {AnnotationModes, MobileViewingModes} from '../../globals/Constants';
import SpotSearchGraphic from '../../assets/SpotSearchGraphic.svg';
import SpotSpeechBubbleGraphic from '../../assets/SpotSpeechBubbleGraphic.svg';
import Themeable from '../../types/Themeable';
import CommentTabs from '../CommentTabs/CommentTabs';
import DetailsPaneContainer from '../DetailsPane/DetailsPaneContainer';

export default class Conversation extends React.Component<ConversationProps, ConversationState> {
   readonly state: ConversationState = {commentTabSelected: true, commentsToHighlight: []};
   scroller: React.RefObject<HTMLDivElement>;
   lastY: number;

   constructor(props: ConversationProps) {
      super(props);
      autoBind(this);
      this.scroller = React.createRef();
      this.lastY = 0;
   }

   componentDidMount() {

      this.scroller.current.addEventListener('touchstart', this.handleTouchStart);
      this.scroller.current.addEventListener('touchmove', this.handleTouchMove);
   }

   componentWillUnmount() {
      this.scroller.current.removeEventListener('touchstart', this.handleTouchStart);
      this.scroller.current.removeEventListener('touchmove', this.handleTouchMove);
   }

   handleTouchStart(e: TouchEvent) {
      this.lastY = e.touches[0].clientY;
   }

   handleTouchMove(e: TouchEvent) {
      const top = e.touches[0].clientY;
      const scroller = this.scroller.current;
      const scrollTop = scroller.scrollTop;
      const isScrollingUp = !!(this.lastY - top < 0);
      const isScrollingAboveTop = (scrollTop === 0 && isScrollingUp);
      const isScrollingBelowBottom = (scrollTop >= (scroller.scrollHeight - scroller.offsetHeight) && !isScrollingUp);
      this.lastY = top;

      if (isScrollingAboveTop || isScrollingBelowBottom) {
         e.preventDefault();
      }
   }

   componentDidUpdate(prevProps: ConversationProps) {
      const {hideEmptyState, visibleComments, searchText} = this.props;
      const {commentTabSelected} = this.state;

      if (!commentTabSelected || hideEmptyState || searchText !== prevProps.searchText) {
         if (this.state.commentsToHighlight.length) {
            this.setState({commentsToHighlight: []});
         }
      } else if (visibleComments !== prevProps.visibleComments) {
         const prevCommentIds = prevProps.visibleComments.map(c => c.id);
         const newCommentsToHighlight = visibleComments.map(c => c.id).filter(id => prevCommentIds.indexOf(id) === -1);
         this.setState({commentsToHighlight: newCommentsToHighlight});
      }
   }

   setCommentTabSelected(isSelected: boolean): void {
      this.setState({commentTabSelected: isSelected});
   }

   setFilter(): void {
      const newHideResolved = !this.props.hideResolved;

      this.props.setCommentFilter(newHideResolved);
      LocalStorage.setItem('ResolutionFilter_' + this.props.reviewId, newHideResolved.toString());
   }

   onClearSearchClick() {
      this.props.setCommentSearchText('');
   }

   getLabelText(unresolvedCommentCount: number): string {
      const {searchText, allComments} = this.props;

      if (searchText) {
         return Strings.emptyStates.comments.noMatchingComments;
      } else if (allComments.length === 0) {
         return Strings.emptyStates.comments.noComments;
      } else if (unresolvedCommentCount === 0) {
         return Strings.emptyStates.comments.noUnresolvedComments;
      }
   }

   onCommentsClick(): void {
      const {annotationMode, setAnnotationMode} = this.props;

      if (annotationMode === AnnotationModes.edit) {
         setAnnotationMode(AnnotationModes.none);
      }
   }

   getCommentsElement() {
      const {visibleComments, showCommentTime, searchText, themeName, hideEmptyState, hideResolved, allComments} = this.props;
      const unresolvedCommentCount = allComments.filter(comment => comment.isResolved === false && !comment.parentCommentId).length;
      const labelText = this.getLabelText(unresolvedCommentCount);
      const svg = searchText ? <SpotSearchGraphic/> : <SpotSpeechBubbleGraphic/>;

      if (visibleComments.length) {
         return (
            <div className={Constants.commentContainerClassName} onClick={this.onCommentsClick}>
               {visibleComments.map(c => <ParentCommentContainer
                  shouldShowUpdate={this.state.commentsToHighlight.indexOf(c.id) !== -1}
                  comment={c}
                  replies={c.replies}
                  key={c.id}
                  showCommentTime={showCommentTime}/>)}
            </div>
         );
      } else if (hideEmptyState) {
         return null;
      } else if (searchText) {
         return (
            <div className="no-comments-container">
               <div className="svg-container">
                  {svg}
               </div>
               <div className="no-comments-label">
                  {labelText}
               </div>
               {searchText && <ControlButton label={Strings.emptyStates.comments.clearSearchButton} buttonType={TscButtonType.secondary} themeName={themeName} onClick={this.onClearSearchClick}/>}
            </div>
         );
      } else {
         return (
            <div className="no-comments-container">
               <div className="svg-container">
                  {svg}
               </div>
               <div className="no-comments-label">
                  {labelText}
               </div>
               {hideResolved && unresolvedCommentCount === 0 && <ControlButton label={Strings.emptyStates.comments.showAllCommentsButton} buttonType={TscButtonType.secondary} themeName={themeName} onClick={this.setFilter}/>}
            </div>
         );
      }
   }

   render() {
      const {setCommentSearchText, hideResolved, searchText, themeName, allComments, mobileViewingMode} = this.props;
      const {commentTabSelected} = this.state;
      const commentsElement = this.getCommentsElement();
      const resolvedCommentCount = allComments.filter(comment => comment.isResolved === true).length;
      const conversationContainerClassNames = mobileViewingMode === MobileViewingModes.feedback ? 'conversation-container hide-on-mobile' : 'conversation-container';

      return (
         <div className={conversationContainerClassNames}>
            <div className="tab-container">
               <CommentTabs setCommentTabSelected={this.setCommentTabSelected} commentTabSelected={commentTabSelected} commentsCount={allComments.length}/>
            </div>
            <div className="conversation" ref={this.scroller}>
               {commentTabSelected && <React.Fragment>
                  {<div className="filter-container">
                     <Search setSearchText={setCommentSearchText} placeholderText={Strings.labels.searchComments} searchText={searchText}/>
                        {resolvedCommentCount !== 0 ?
                           <ControlButton label={!hideResolved ? Strings.labels.hideResolvedComments.replace('{param0}', resolvedCommentCount.toString()) : Strings.labels.showResolvedComments.replace('{param0}', resolvedCommentCount.toString())}
                                          onClick={this.setFilter}
                                          buttonType={TscButtonType.secondary}
                                          themeName={themeName}/> :
                           <div className="no-resolved-comments-placeholder">{Strings.labels.noResolvedComments}</div>
                        }
                  </div>}
                  {commentsElement}
               </React.Fragment>}
               {!commentTabSelected && <DetailsPaneContainer/>}
            </div>
         </div>
      );
   }
}

export interface ConversationProps extends Themeable {
   annotationMode: AnnotationModes;
   reviewId: string;
   visibleComments: CommentModel[];
   allComments: CommentModel[];
   showCommentTime: boolean;
   hideResolved: boolean;
   searchText: string;
   hideEmptyState: boolean;
   mobileViewingMode: MobileViewingModes;
   setCommentSearchText(text: string): void;
   setCommentFilter(filter: boolean): void;
   setAnnotationMode(annotationMode: AnnotationModes): void;
}

export interface ConversationState {
   commentTabSelected: boolean;
   commentsToHighlight: string[];
}
